import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Contact.css';
import useLanguage from '../hooks/useLanguage';

const Contact = () => {
  const { t, isHebrew, language, translations } = useLanguage();
  const iconSpacing = isHebrew ? 'ms-2' : 'me-2';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'contact',
          ...formData
        }).toString()
      });
      if (!res.ok) throw new Error('Network response was not ok');
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const handleWhatsApp = () => {
    const template = translations.contact.whatsAppTemplate[language];
    const text = encodeURIComponent(
      template
        .replace('{name}', formData.name)
        .replace('{email}', formData.email)
        .replace('{phone}', formData.phone)
        .replace('{message}', formData.message)
    );
    window.open(`https://wa.me/972587681185?text=${text}`, '_blank');
  };

  if (status === 'success') {
    return (
      <section id="contact" className="contact-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center mb-5">
              <h2 className="display-5 fw-bold text-dark mb-3">{t('contact', 'title')}</h2>
              <p className="lead text-muted">{t('contact', 'subtitle')}</p>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="success-message" data-aos="fade-up">
                <i className="bi bi-check-circle-fill"></i>
                <h3>{t('contact', 'successTitle')}</h3>
                <p>{t('contact', 'successText')}</p>
                <button
                  className="btn btn-outline-primary mt-3"
                  onClick={() => setStatus('idle')}
                >
                  {t('contact', 'sendAnother')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="contact-section py-5">
      <div className="container">
        <div className="row">
          <div className="col-12 text-center mb-5">
            <h2 className="display-5 fw-bold text-dark mb-3">{t('contact', 'title')}</h2>
            <p className="lead text-muted">
              {t('contact', 'subtitle')}
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-8 mb-4">
            <div className="contact-form-container" data-aos="fade-up">
              <h3 className="mb-4">{t('contact', 'formTitle')}</h3>

              {status === 'error' && (
                <div className="error-message mb-3">
                  <i className={`bi bi-exclamation-triangle-fill ${iconSpacing}`}></i>
                  {t('contact', 'errorMessage')}
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                {/* Honeypot field - hidden from humans, catches bots */}
                <p className="d-none">
                  <label>
                    Don't fill this out: <input name="bot-field" />
                  </label>
                </p>
                <input type="hidden" name="form-name" value="contact" />

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="name" className="form-label">{t('contact', 'labelName')}</label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="email" className="form-label">{t('contact', 'labelEmail')}</label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="phone" className="form-label">{t('contact', 'labelPhone')}</label>
                  <input
                    type="tel"
                    className="form-control"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="message" className="form-label">{t('contact', 'labelMessage')}</label>
                  <textarea
                    className="form-control"
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>

                <div className="form-buttons">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? (
                      <>
                        <span className={`spinner-border spinner-border-sm ${iconSpacing}`} role="status" aria-hidden="true"></span>
                        {t('contact', 'sending')}
                      </>
                    ) : (
                      <>
                        <i className={`bi bi-envelope-fill ${iconSpacing}`}></i>
                        {t('contact', 'sendEmail')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-whatsapp btn-lg"
                    onClick={handleWhatsApp}
                    disabled={status === 'sending' || !formData.name || !formData.message}
                  >
                    <i className={`bi bi-whatsapp ${iconSpacing}`}></i>
                    {t('contact', 'sendWhatsApp')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="contact-info" data-aos="fade-up">
              <h3 className="mb-4">{t('contact', 'contactDetails')}</h3>

              <div className="contact-methods mb-4">
                <div className="contact-method">
                  <i className="bi bi-telephone-fill text-primary"></i>
                  <div>
                    <h5>{t('contact', 'phoneIL')}</h5>
                    <p dir="ltr">058-768-1185</p>
                  </div>
                </div>

                <div className="contact-method">
                  <i className="bi bi-telephone-fill text-primary"></i>
                  <div>
                    <h5>{t('contact', 'phoneUS')}</h5>
                    <p dir="ltr">+1 (516) 323-7928</p>
                  </div>
                </div>

                <div className="contact-method">
                  <i className="bi bi-envelope-fill text-primary"></i>
                  <div>
                    <h5>{t('contact', 'email')}</h5>
                    <p>Bentzy@levitztax.com</p>
                  </div>
                </div>

                <div className="contact-method">
                  <i className="bi bi-globe text-primary"></i>
                  <div>
                    <h5>{t('contact', 'website')}</h5>
                    <p><a href="https://www.levitztax.com">www.levitztax.com</a></p>
                  </div>
                </div>

                <div className="contact-method">
                  <i className="bi bi-geo-alt-fill text-primary"></i>
                  <div>
                    <h5>{t('contact', 'address')}</h5>
                    <p><span dir="ltr">Kanfei Nesharim 68, Jerusalem, Israel</span></p>
                  </div>
                </div>
              </div>

              <div className="offices">
                <h4 className="mb-3">{t('contact', 'ourOffice')}</h4>
                <div className="office-card mb-3">
                  <h5 className="office-city">{t('contact', 'officeCity')}</h5>
                  <p className="office-manager">Bentzy Levitz, U.S. Tax Professional</p>
                  <p className="office-address">
                    <i className={`bi bi-geo-alt ${iconSpacing}`}></i>
                    <span dir="ltr">Kanfei Nesharim 68, Jerusalem, Israel</span>
                  </p>
                  <p className="office-phone">
                    <i className={`bi bi-telephone ${iconSpacing}`}></i>
                    <span dir="ltr">058-768-1185</span>
                  </p>
                  <p className="office-phone">
                    <i className={`bi bi-telephone ${iconSpacing}`}></i>
                    <span dir="ltr">+1 (516) 323-7928 (USA)</span>
                  </p>
                  <p className="office-email">
                    <i className={`bi bi-envelope ${iconSpacing}`}></i>
                    Bentzy@levitztax.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
